import React, { useState, useEffect } from 'react';
import { fetchProducts } from '../utils/products';
import { Product } from '../types/product';
import { getApiUrl } from '../config/api';

const StripeDebug: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rawResponse, setRawResponse] = useState<any>(null);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(getApiUrl('stripe_products.php'));
        const rawData = await response.json();
        setRawResponse(rawData);
        const productsData = await fetchProducts();
        setProducts(productsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  if (loading) {
    return <div className="p-8 text-center">Loading Stripe data...</div>;
  }

  if (error) {
    return <div className="p-8 text-red-600">Error: {error}</div>;
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-8 text-center">Stripe Products Debug</h1>
      
      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Raw API Response</h2>
        <pre className="bg-gray-100 p-4 rounded-lg overflow-auto text-sm">
          {JSON.stringify(rawResponse, null, 2)}
        </pre>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Products Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-blue-100 p-4 rounded-lg">
            <h3 className="font-semibold">Total Products</h3>
            <p className="text-2xl font-bold">{products.length}</p>
          </div>
          <div className="bg-green-100 p-4 rounded-lg">
            <h3 className="font-semibold">Subscriptions</h3>
            <p className="text-2xl font-bold">{products.filter(p => p.isSubscription).length}</p>
          </div>
          <div className="bg-purple-100 p-4 rounded-lg">
            <h3 className="font-semibold">One-time</h3>
            <p className="text-2xl font-bold">{products.filter(p => !p.isSubscription).length}</p>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Individual Products</h2>
        <div className="space-y-4">
          {products.map((product, index) => (
            <div key={product.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-semibold">{product.name}</h3>
                <span className={`px-2 py-1 rounded text-sm ${
                  product.isSubscription 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {product.isSubscription ? 'Subscription' : 'One-time'}
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <strong>ID:</strong> {product.id}
                </div>
                <div>
                  <strong>Price:</strong> {product.price} {product.currency?.toUpperCase()}
                </div>
                <div>
                  <strong>Stripe Price ID:</strong> {product.stripePriceId}
                </div>
                <div>
                  <strong>Rating:</strong> {product.rating} ({product.reviews} reviews)
                </div>
                <div className="col-span-2">
                  <strong>Description:</strong> {product.description || 'No description'}
                </div>
                <div className="col-span-2">
                  <strong>Images:</strong> {product.images?.length || 0} images
                </div>
                <div className="col-span-2">
                  <strong>Metadata:</strong> {Object.keys(product.metadata || {}).length} items
                </div>
              </div>
              
              {product.images && product.images.length > 0 && (
                <div className="mt-2">
                  <strong>Images:</strong>
                  <div className="flex gap-2 mt-1">
                    {product.images.map((img, imgIndex) => (
                      <img 
                        key={imgIndex} 
                        src={img} 
                        alt={`${product.name} ${imgIndex + 1}`}
                        className="w-16 h-16 object-cover rounded border"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StripeDebug;
